import {
  Cross2Icon,
  ExclamationTriangleIcon,
  PlusIcon,
  TrashIcon,
} from '@radix-ui/react-icons';
import { useTranslation } from 'react-i18next';
import { IContractVariable } from '@clickersphere/core/entities/IContractVariable';
import { ContractVariableSourceEnum } from '@clickersphere/core/enums/ContractVariableSourceEnum';
import { Button } from '../../Button';
import Checkbox from '../../Checkbox';
import { IconButton } from '../../IconButton';
import { Input } from '../../Input';
import Select from '../../Select';
import { TextButton } from '../../TextButton';
import { variableSourceLabelKeys } from '../variableSourceLabelKeys';

export interface IEditableContractVariable extends IContractVariable {
  rowId: string;
}

export interface IVariableRowErrors {
  key?: string;
  label?: string;
}

interface IVariableMappingEditorProps {
  variables: IEditableContractVariable[];
  errors: Record<string, IVariableRowErrors>;
  disabled?: boolean;
  onChange: (rowId: string, patch: Partial<IContractVariable>) => void;
  onRemove: (rowId: string) => void;
  onRemoveAnchor: (rowId: string, anchorIndex: number) => void;
  onLocateVariable: (varKey: string) => void;
  onAdd: () => void;
}

export default function VariableMappingEditor({
  variables,
  errors,
  disabled,
  onChange,
  onRemove,
  onRemoveAnchor,
  onLocateVariable,
  onAdd,
}: IVariableMappingEditorProps) {
  const { t } = useTranslation();

  const sourceOptions = Object.values(ContractVariableSourceEnum).map(
    (source) => ({
      value: source,
      text: t(variableSourceLabelKeys[source]),
    }),
  );

  return (
    <section className="flex flex-col gap-3">
      <div className="flex flex-col gap-1">
        <h3 className="text-text-primary text-sm font-semibold">
          {t('contract.templates.variables.title')}
        </h3>
        <p className="text-text-secondary text-sm">
          {t('contract.templates.variables.description')}
        </p>
      </div>

      {variables.length === 0 && (
        <p className="text-text-tertiary py-2 text-sm">
          {t('contract.templates.variables.empty')}
        </p>
      )}

      {variables.length > 0 && (
        <ul className="flex flex-col gap-3">
          {variables.map((variable) => {
            const anchorCount = variable.anchors.length;
            const keyReadOnly = anchorCount > 0;

            return (
              <li
                key={variable.rowId}
                className="border-border-default bg-surface-elevated flex flex-col gap-3 rounded-lg border p-3"
              >
                <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                  <Input
                    type="text"
                    name={`variable-key-${variable.rowId}`}
                    placeholder={t('contract.templates.variables.keyField')}
                    value={variable.key}
                    error={errors[variable.rowId]?.key}
                    disabled={disabled}
                    readOnly={keyReadOnly}
                    onChange={(event) =>
                      onChange(variable.rowId, { key: event.target.value })
                    }
                  />
                  <Input
                    type="text"
                    name={`variable-label-${variable.rowId}`}
                    placeholder={t('contract.templates.variables.labelField')}
                    value={variable.label}
                    error={errors[variable.rowId]?.label}
                    disabled={disabled}
                    onChange={(event) =>
                      onChange(variable.rowId, { label: event.target.value })
                    }
                  />
                </div>

                {keyReadOnly && (
                  <p className="text-text-tertiary text-xs">
                    {t('contract.templates.variables.keyHint')}
                  </p>
                )}

                <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                  <div className="w-full sm:max-w-60">
                    <Select
                      placeholder={t(
                        'contract.templates.variables.sourceField',
                      )}
                      options={sourceOptions}
                      value={variable.source}
                      disabled={disabled}
                      onChange={(value) =>
                        onChange(variable.rowId, {
                          source: value as ContractVariableSourceEnum,
                        })
                      }
                    />
                  </div>

                  <div className="flex items-center justify-between gap-3 sm:justify-end">
                    <label
                      htmlFor={`variable-required-${variable.rowId}`}
                      className="text-text-primary flex h-10 cursor-pointer items-center gap-2 text-sm"
                    >
                      <Checkbox
                        id={`variable-required-${variable.rowId}`}
                        checked={variable.required}
                        disabled={disabled}
                        onCheckedChange={(checked) =>
                          onChange(variable.rowId, { required: checked })
                        }
                      />
                      {t('contract.templates.variables.requiredField')}
                    </label>

                    <IconButton
                      size="md"
                      variant="danger"
                      className="h-10 w-10"
                      aria-label={t('contract.templates.variables.remove')}
                      disabled={disabled}
                      onClick={() => onRemove(variable.rowId)}
                    >
                      <TrashIcon className="size-4" />
                    </IconButton>
                  </div>
                </div>

                {anchorCount === 0 ? (
                  <p className="flex items-center gap-1 rounded-md bg-yellow-50 px-2 py-1 text-xs text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-200">
                    <ExclamationTriangleIcon className="size-3.5 shrink-0" />
                    {t('contract.templates.variables.notPositioned')}
                  </p>
                ) : (
                  <div className="flex flex-col gap-1">
                    <TextButton
                      size="xs"
                      underlineOnHover
                      className="w-fit gap-1"
                      disabled={disabled}
                      aria-label={t('contract.templates.variables.locate')}
                      onClick={() => onLocateVariable(variable.key)}
                    >
                      {t('contract.templates.variables.anchorsCount', {
                        count: anchorCount,
                      })}
                    </TextButton>
                    <ul className="flex flex-wrap gap-2">
                      {variable.anchors.map((anchor, anchorIndex) => (
                        <li
                          key={`${anchor.paragraphIndex}-${anchor.matchText}-${anchorIndex}`}
                          className="bg-surface-input text-text-secondary flex max-w-full items-center gap-1 rounded-full py-1 pr-1 pl-2 text-xs"
                        >
                          <span className="max-w-40 truncate">
                            {anchor.matchText}
                          </span>
                          <IconButton
                            size="md"
                            variant="danger"
                            className="h-10 w-10"
                            aria-label={t(
                              'contract.templates.variables.removeAnchor',
                            )}
                            disabled={disabled}
                            onClick={() =>
                              onRemoveAnchor(variable.rowId, anchorIndex)
                            }
                          >
                            <Cross2Icon className="size-3.5" />
                          </IconButton>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </li>
            );
          })}
        </ul>
      )}

      <Button
        className="h-9 w-full text-sm sm:w-auto sm:px-4"
        color="outline"
        disabled={disabled}
        onClick={onAdd}
      >
        <PlusIcon className="mr-1 size-4" />
        {t('contract.templates.variables.add')}
      </Button>
    </section>
  );
}
